import axios from "axios";
import { toast } from "react-toastify";

const LOGIN_PATH_PATTERN = /signIn|forgetPassword|verifyOTP|resetPassword/i;

const getErrorMessage = (data = {}) => data?.errorMessage || data?.message || "";

const isLoginEndpoint = (url = "") => LOGIN_PATH_PATTERN.test(url);

const isInvalidCredentials = (message = "") => message === "Invalid credentials";

const isAdminNotFound = (message = "") => message === "Admin not found";

const isTokenInvalidMessage = (message = "") => {
  const normalized = String(message).toLowerCase().trim();
  return (
    normalized.includes("token invalid") ||
    normalized.includes("token is invalid") ||
    normalized.includes("invalid token") ||
    normalized === "your token is invalid!"
  );
};

const isSessionExpiredMessage = (message = "") => {
  const normalized = String(message).toLowerCase().trim();
  return (
    isTokenInvalidMessage(message) ||
    normalized.includes("session expired") ||
    normalized === "something went wrong"
  );
};

const shouldForceLogout = ({ message = "", code, status, url = "" }) => {
  if (isLoginEndpoint(url)) {
    return false;
  }

  if (isInvalidCredentials(message) || isAdminNotFound(message)) {
    return false;
  }

  if (code === 401 || status === 401) {
    return true;
  }

  if (code === 403 || status === 403) {
    return true;
  }

  if (isTokenInvalidMessage(message) || isSessionExpiredMessage(message)) {
    return true;
  }

  if (status === 404 && (isTokenInvalidMessage(message) || message === "Something went wrong")) {
    return true;
  }

  return false;
};

const handleSessionExpired = (message) => {
  const displayMessage = message || "Your session has expired. Please log in again.";

  toast.error(displayMessage);
  localStorage.removeItem("sessionToken");
  localStorage.removeItem("lastActivityTime");

  setTimeout(() => {
    window.location.href = "/";
  }, 1500);

  return { error: true, authError: true, message: displayMessage };
};

const checkResponseForAuthError = (response, url) => {
  const data = response?.data || {};
  const message = getErrorMessage(data);
  const code = data?.code;

  if (
    shouldForceLogout({
      message,
      code,
      status: response?.status,
      url,
    })
  ) {
    return handleSessionExpired(message);
  }

  return null;
};

export const handleApiCall = async (method = "", url = "", data = {}, token = "") => {
  const axiosConfig = {
    method,
    url: `${process.env.REACT_APP_API}${url}`,
    headers: {
      Authorization: `Bearer ${localStorage?.getItem("sessionToken") || token}`,
    },
  };

  if (method !== "get") {
    axiosConfig.data = data;
  }

  try {
    const response = await axios(axiosConfig);

    const authError = checkResponseForAuthError(response, url);
    if (authError) {
      return authError;
    }

    return response;
  } catch (error) {
    console.warn(error);

    const errorData = error?.response?.data || {};
    const errorMsg = getErrorMessage(errorData) || "Something went wrong!";
    const errorCode = errorData?.code;
    const statusCode = error?.response?.status;

    if (isLoginEndpoint(url)) {
      if (isInvalidCredentials(errorMsg) || isAdminNotFound(errorMsg)) {
        toast.error(errorMsg);
        return { error: true, message: errorMsg };
      }
    }

    if (
      shouldForceLogout({
        message: errorMsg,
        code: errorCode,
        status: statusCode,
        url,
      })
    ) {
      return handleSessionExpired(errorMsg);
    }

    toast.error(errorMsg);
    return { error: true, message: errorMsg };
  }
};
